/**
 * UTM Propagation Helpers
 *
 * Extracts, persists, and forwards UTM parameters across navigation.
 * Reference: site/docs/audits/MAPPITO_WEBSITE_PLAN.md §13.3 UTM Preservation Checklist
 *
 * UTM params must be preserved through:
 *  - open-app attempts,
 *  - install fallback pages,
 *  - analytics events.
 */

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

/** The five standard UTM keys. */
export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

/** SessionStorage key for persisted UTMs. */
const STORAGE_KEY = "mappito_utm";

/* -------------------------------------------------------------------------- */
/*  Extract                                                                    */
/* -------------------------------------------------------------------------- */

/** Extract UTM params from a URL string or URLSearchParams. */
export function extractUtmParams(
  input: string | URLSearchParams,
): UtmParams {
  const params =
    typeof input === "string" ? new URL(input, "https://x").searchParams : input;

  const result: UtmParams = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) {
      result[key] = value;
    }
  }
  return result;
}

/** Check if any UTM params are present. */
export function hasUtmParams(params: UtmParams): boolean {
  return UTM_KEYS.some((k) => !!params[k]);
}

/* -------------------------------------------------------------------------- */
/*  Persist (client-side session storage)                                      */
/* -------------------------------------------------------------------------- */

/** Save UTM params to sessionStorage (client only). Merges with existing. */
export function persistUtmParams(params: UtmParams): void {
  if (typeof window === "undefined") return;
  try {
    const existing = getPersistedUtmParams();
    const merged = { ...existing, ...params };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    /* storage not available — silently ignore */
  }
}

/** Retrieve persisted UTM params from sessionStorage. */
export function getPersistedUtmParams(): UtmParams {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as UtmParams;
  } catch {
    return {};
  }
}

/** Clear persisted UTM params. */
export function clearPersistedUtmParams(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/* -------------------------------------------------------------------------- */
/*  Append to URL                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Append UTM params to a URL string.  Existing query params are preserved.
 * Only non-empty UTM values are appended.
 */
export function appendUtmParams(url: string, params: UtmParams): string {
  if (!hasUtmParams(params)) return url;

  const parsed = new URL(url, "https://www.mappito.com");
  for (const key of UTM_KEYS) {
    const value = params[key];
    if (value) {
      parsed.searchParams.set(key, value);
    }
  }

  // Return path + query for relative URLs, full URL for absolute
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return parsed.toString();
  }
  return `${parsed.pathname}${parsed.search}`;
}

/* -------------------------------------------------------------------------- */
/*  Map to EventContext fields                                                  */
/* -------------------------------------------------------------------------- */

import type { EventContext } from "./events";

/** Convert UtmParams to EventContext UTM fields. */
export function utmToEventContext(params: UtmParams): Pick<
  EventContext,
  "utmSource" | "utmMedium" | "utmCampaign" | "utmTerm" | "utmContent"
> {
  return {
    utmSource: params.utm_source,
    utmMedium: params.utm_medium,
    utmCampaign: params.utm_campaign,
    utmTerm: params.utm_term,
    utmContent: params.utm_content,
  };
}
